import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getProductBySlug } from "@/lib/api-client";
import { formatXOF } from "@/lib/format";
import {
  BLOCKING_PRICE_CODE,
  BLOCKING_PRICE_LABEL,
  BLOCKING_TIMING_CODE,
  BLOCKING_TIMING_LABEL,
  BLOCKING_ZONE_CODE,
  BLOCKING_ZONE_LABEL,
  getCampaignChoices,
  isBlockingCode,
  submitQuizLead,
} from "@/lib/campaign-lead";

/**
 * Page d'atterrissage publicitaire — `/offre/:slug`.
 *
 * Destinée à recevoir le clic d'une publicité Facebook/Instagram, elle
 * reproduit le parcours du formulaire instantané Meta (« VVS - Quiz Abidjan »)
 * mais sur notre propre domaine : vidéo du produit, prix, description courte,
 * un seul bouton « Commander », puis le questionnaire de qualification.
 *
 * Pourquoi une page dédiée plutôt que la fiche produit `/p/:slug` : la fiche
 * produit propose tout le site (navigation, catégories, produits liés, panier)
 * — autant d'occasions de partir ailleurs. Ici le visiteur n'a qu'une seule
 * action possible, et chaque réponse disqualifiante arrête le parcours au lieu
 * d'envoyer un prospect inutile sur le WhatsApp du vendeur.
 *
 * Le produit (nom, prix, vidéo, images) est lu depuis l'API catalogue : rien
 * n'est codé en dur, la même page sert donc n'importe quel produit du
 * catalogue sans modification.
 */

/**
 * Chrome minimal de la page de campagne : logo + rappel de réassurance, et
 * rien d'autre. Volontairement SANS `ShopLayout` (en-tête de navigation, pied
 * de page complet, barre d'onglets mobile, bouton WhatsApp flottant) : chacun
 * de ces éléments est une sortie possible vers une autre page, alors qu'une
 * page d'atterrissage publicitaire n'a qu'une seule action à proposer. La
 * barre d'onglets fixe entrait en plus en collision avec le bouton
 * « Commander » fixe de cette page (mêmes `bottom-0` et `z-40`).
 */
function CampaignShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <header className="border-b border-line bg-white">
        <div className="container-vvs flex h-14 items-center justify-between sm:h-16">
          <a href="/" aria-label="VVS — Vendez Vouse Store, retour à l'accueil">
            <img src="/logo-footer.png" alt="VVS — Vendez Vouse Store" className="h-8 w-auto sm:h-9" />
          </a>
          <span className="font-body text-xs uppercase tracking-wide text-muted">
            Paiement à la livraison
          </span>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-line bg-white">
        <div className="container-vvs py-5 text-center font-body text-xs leading-relaxed text-muted">
          VVS — Vendez-Vous eShop · Yopougon Ananeraie, Abidjan
          <br />
          <a href="/confidentialite" className="underline hover:text-ink">
            Politique de confidentialité
          </a>
          <span aria-hidden="true"> · </span>
          <a href="/cgv" className="underline hover:text-ink">
            Conditions de vente
          </a>
        </div>
      </footer>
    </div>
  );
}

type Step = "landing" | "form" | "blocked" | "done";

const SELECT_CLASSES =
  "mt-1 w-full rounded-sm border border-line bg-white px-3 py-3 font-body text-sm text-ink " +
  "focus:border-gold focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-gold";

const INPUT_CLASSES = SELECT_CLASSES;

const LABEL_CLASSES = "block font-body text-xs font-semibold uppercase tracking-wide text-navy";

/** Pour une question rédigée en phrase complète : ni majuscules ni interlettrage. */
const SENTENCE_LABEL_CLASSES = "block font-body text-sm font-semibold leading-snug text-navy";

export function OffrePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [step, setStep] = useState<Step>("landing");

  const [zoneCode, setZoneCode] = useState("");
  const [timing, setTiming] = useState("");
  const [priceAnswer, setPriceAnswer] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = useState<string | null>(null);

  const productQuery = useQuery({
    queryKey: ["catalog", "product", slug],
    queryFn: () => getProductBySlug(slug),
    enabled: slug.length > 0,
  });

  const choicesQuery = useQuery({
    queryKey: ["campaign", "choices"],
    queryFn: getCampaignChoices,
  });

  const product = productQuery.data;

  const video = useMemo(() => product?.media.find((m) => m.type === "VIDEO") ?? null, [product]);
  const poster = useMemo(
    () => product?.media.find((m) => m.type === "IMAGE" && m.isPrimary) ?? product?.media.find((m) => m.type === "IMAGE") ?? null,
    [product],
  );

  const variant = useMemo(
    () => product?.variants.find((v) => v.isDefault) ?? product?.variants[0] ?? null,
    [product],
  );
  const priceLabel = variant ? (variant.price.formatted ?? formatXOF(variant.price.amountMinor)) : null;

  const selectedZone = choicesQuery.data?.zones.find((z) => z.code === zoneCode) ?? null;
  const deliveryLabel =
    selectedZone === null
      ? null
      : selectedZone.feeMinor === null
        ? "à confirmer avec le conseiller"
        : formatXOF(selectedZone.feeMinor);

  const totalLabel =
    variant && selectedZone && selectedZone.feeMinor !== null
      ? formatXOF(variant.price.amountMinor + selectedZone.feeMinor)
      : null;

  /** Affiche le questionnaire et amène le visiteur dessus une fois monté. */
  function openForm(): void {
    setStep("form");
    requestAnimationFrame(() => {
      document.getElementById("offre-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function handleChoice(setter: (value: string) => void, value: string): void {
    setter(value);
    if (isBlockingCode(value)) setStep("blocked");
  }

  function resetBlocking(): void {
    if (isBlockingCode(zoneCode)) setZoneCode("");
    if (isBlockingCode(timing)) setTiming("");
    if (isBlockingCode(priceAnswer)) setPriceAnswer("");
    setStep("form");
  }

  const formReady =
    zoneCode.length > 0 &&
    !isBlockingCode(zoneCode) &&
    timing.length > 0 &&
    !isBlockingCode(timing) &&
    priceAnswer.length > 0 &&
    !isBlockingCode(priceAnswer) &&
    fullName.trim().length >= 2 &&
    phone.replace(/\D/g, "").length >= 8;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!formReady || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitQuizLead({
        productSlug: slug,
        fullName: fullName.trim(),
        phone: phone.trim(),
        zoneCode,
        timing,
        priceAnswer,
        source: "page-offre",
      });
      setWhatsappLink(result.whatsappLink);
      setStep("done");
      // Redirection dans l'onglet courant (et non `window.open`, bloqué par
      // les navigateurs après un `await`) : c'est le client qui ouvre la
      // conversation, donc le message arrive comme message ENTRANT côté
      // boutique. L'écran « done » reste affiché s'il revient en arrière.
      window.location.href = result.whatsappLink;
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "Envoi impossible pour le moment.");
    } finally {
      setSubmitting(false);
    }
  }

  // --- États de chargement / erreur ----------------------------------------

  if (productQuery.isPending) {
    return (
      <CampaignShell>
        <div className="container-vvs py-16 text-center font-body text-sm text-muted">
          Chargement de l&apos;offre…
        </div>
      </CampaignShell>
    );
  }

  if (productQuery.isError || !product) {
    return (
      <CampaignShell>
      <div className="container-vvs py-16 text-center">
        <h1 className="font-display text-xl font-bold text-navy">Offre indisponible</h1>
        <p className="mt-2 font-body text-sm text-muted">
          Cette offre n&apos;existe pas ou n&apos;est plus en ligne.
        </p>
        <a
          href="/catalogue"
          className="mt-6 inline-flex items-center justify-center rounded-sm bg-gold px-6 py-3 font-display text-sm font-semibold text-navy transition-colors hover:bg-goldl"
        >
          Voir la boutique
        </a>
      </div>
      </CampaignShell>
    );
  }

  // --- Écran de sortie (réponse disqualifiante) ----------------------------

  if (step === "blocked") {
    return (
      <CampaignShell>
      <div className="container-vvs py-12 lg:py-16">
        <div className="mx-auto max-w-md rounded-sm border border-line bg-white p-6 text-center sm:p-8">
          <h1 className="font-display text-xl font-bold text-navy">Merci de votre intérêt&nbsp;!</h1>
          <p className="mt-3 font-body text-sm leading-relaxed text-muted">
            Nous livrons uniquement à Abidjan et ses environs, avec paiement à la livraison après
            vérification du produit. Vous pouvez découvrir tous nos produits sur la boutique.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href="/catalogue"
              className="inline-flex items-center justify-center rounded-sm bg-gold px-6 py-3 font-display text-sm font-semibold text-navy transition-colors hover:bg-goldl"
            >
              Voir la boutique
            </a>
            <button
              type="button"
              onClick={resetBlocking}
              className="inline-flex items-center justify-center rounded-sm border border-navy px-6 py-3 font-display text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-cream"
            >
              Corriger ma réponse
            </button>
          </div>
        </div>
      </div>
      </CampaignShell>
    );
  }

  // --- Confirmation --------------------------------------------------------

  if (step === "done") {
    return (
      <CampaignShell>
      <div className="container-vvs py-12 lg:py-16">
        <div className="mx-auto max-w-md rounded-sm border border-line bg-white p-6 text-center sm:p-8">
          <h1 className="font-display text-xl font-bold text-navy">
            C&apos;est noté, {fullName.split(" ")[0]}&nbsp;!
          </h1>
          <p className="mt-3 font-body text-sm leading-relaxed text-muted">
            Votre demande est bien arrivée. Ouvrez la conversation WhatsApp pour que notre
            conseillère confirme votre adresse et l&apos;heure de livraison. Vous payez au livreur,
            après avoir vérifié le produit.
          </p>
          {whatsappLink ? (
            <a
              href={whatsappLink}
              className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-gold px-6 py-4 font-display text-sm font-semibold text-navy transition-colors hover:bg-goldl"
            >
              Ouvrir WhatsApp
            </a>
          ) : null}
        </div>
      </div>
      </CampaignShell>
    );
  }

  // --- Page d'offre + formulaire -------------------------------------------

  return (
    <CampaignShell>
    <div className="pb-28 lg:pb-16">
      <div className="container-vvs py-6 lg:py-10">
        <div className="mx-auto max-w-5xl lg:grid lg:grid-cols-2 lg:items-start lg:gap-10">
          {/* Média produit */}
          <div className="overflow-hidden rounded-sm border border-line bg-navy">
            {video ? (
              <video
                className="mx-auto block max-h-[70vh] w-full object-contain"
                src={video.url}
                poster={poster?.url}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
            ) : poster ? (
              <img
                className="mx-auto block max-h-[70vh] w-full object-contain"
                src={poster.url}
                alt={poster.alt ?? product.name}
                loading="eager"
              />
            ) : null}
          </div>

          {/* Argumentaire */}
          <div className="mt-6 lg:mt-0">
            <h1 className="font-display text-2xl font-bold leading-tight text-navy sm:text-3xl">
              {product.name}
            </h1>

            {priceLabel ? (
              <p className="mt-3 inline-flex items-baseline gap-2 rounded-sm bg-navy px-4 py-2">
                <span className="font-display text-2xl font-bold text-gold">{priceLabel}</span>
                <span className="font-body text-xs uppercase tracking-wide text-cream2">
                  + livraison
                </span>
              </p>
            ) : null}

            {product.subtitle ? (
              <p className="mt-4 font-body text-sm leading-relaxed text-ink">{product.subtitle}</p>
            ) : null}

            {product.descriptionShort ? (
              <p className="mt-2 font-body text-sm leading-relaxed text-muted">
                {product.descriptionShort}
              </p>
            ) : null}

            <ul className="mt-5 space-y-2 font-body text-sm text-ink">
              <li className="flex gap-2">
                <span aria-hidden="true" className="text-gold">✓</span>
                Paiement à la livraison, après vérification du produit
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true" className="text-gold">✓</span>
                Livraison à Abidjan en 24 à 48&nbsp;h, de 1 000 à 2 500&nbsp;FCFA selon la commune
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true" className="text-gold">✓</span>
                Un conseiller vous répond sur WhatsApp après votre demande
              </li>
            </ul>

            {step === "landing" ? (
              <button
                type="button"
                onClick={openForm}
                className="mt-7 hidden w-full items-center justify-center rounded-sm bg-gold px-6 py-4 font-display text-base font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-goldl lg:inline-flex"
              >
                Commander ce produit
              </button>
            ) : null}
          </div>
        </div>

        {/* Formulaire de qualification */}
        {step === "form" ? (
          <form
            id="offre-form"
            onSubmit={handleSubmit}
            className="mx-auto mt-8 max-w-xl rounded-sm border border-line bg-white p-5 sm:p-7"
          >
            <h2 className="font-display text-lg font-bold text-navy">
              Trois questions, et c&apos;est commandé
            </h2>
            <p className="mt-1 font-body text-xs leading-relaxed text-muted">
              Elles nous servent à calculer vos frais de livraison et à vous joindre. Votre numéro
              WhatsApp sert uniquement à confirmer la commande.
            </p>

            {choicesQuery.isError ? (
              <p className="mt-4 rounded-sm border border-line bg-cream2 px-3 py-2 font-body text-sm text-ink">
                Le questionnaire est momentanément indisponible. Réessayez dans un instant.
              </p>
            ) : null}

            <div className="mt-5 space-y-5">
              <div>
                <label className={LABEL_CLASSES} htmlFor="offre-zone">
                  Où voulez-vous être livré(e)&nbsp;?
                </label>
                <select
                  id="offre-zone"
                  className={SELECT_CLASSES}
                  value={zoneCode}
                  onChange={(e) => handleChoice(setZoneCode, e.target.value)}
                  disabled={choicesQuery.isPending}
                  required
                >
                  <option value="">Choisissez votre commune…</option>
                  {choicesQuery.data?.zones.map((zone) => (
                    <option key={zone.code} value={zone.code}>
                      {zone.feeMinor === null ? zone.label : `${zone.label} — ${formatXOF(zone.feeMinor)}`}
                    </option>
                  ))}
                  <option value={BLOCKING_ZONE_CODE}>{BLOCKING_ZONE_LABEL}</option>
                </select>
                {deliveryLabel ? (
                  <p className="mt-1 font-body text-xs text-muted">
                    Frais de livraison&nbsp;: {deliveryLabel}
                    {totalLabel ? <> — total estimé <strong className="text-ink">{totalLabel}</strong></> : null}
                  </p>
                ) : null}
              </div>

              <div>
                <label className={LABEL_CLASSES} htmlFor="offre-timing">
                  Quand souhaitez-vous le recevoir&nbsp;?
                </label>
                <select
                  id="offre-timing"
                  className={SELECT_CLASSES}
                  value={timing}
                  onChange={(e) => handleChoice(setTiming, e.target.value)}
                  disabled={choicesQuery.isPending}
                  required
                >
                  <option value="">Choisissez…</option>
                  {choicesQuery.data?.timings.map((choice) => (
                    <option key={choice.code} value={choice.code}>
                      {choice.label}
                    </option>
                  ))}
                  <option value={BLOCKING_TIMING_CODE}>{BLOCKING_TIMING_LABEL}</option>
                </select>
              </div>

              <div>
                <label className={SENTENCE_LABEL_CLASSES} htmlFor="offre-price">
                  {priceLabel ? `${priceLabel} + livraison, payé au livreur après vérification. C'est d'accord ?` : "Le prix vous convient ?"}
                </label>
                <select
                  id="offre-price"
                  className={SELECT_CLASSES}
                  value={priceAnswer}
                  onChange={(e) => handleChoice(setPriceAnswer, e.target.value)}
                  disabled={choicesQuery.isPending}
                  required
                >
                  <option value="">Choisissez…</option>
                  {choicesQuery.data?.priceAnswers.map((choice) => (
                    <option key={choice.code} value={choice.code}>
                      {choice.label}
                    </option>
                  ))}
                  <option value={BLOCKING_PRICE_CODE}>{BLOCKING_PRICE_LABEL}</option>
                </select>
              </div>

              <div>
                <label className={LABEL_CLASSES} htmlFor="offre-name">
                  Votre nom
                </label>
                <input
                  id="offre-name"
                  className={INPUT_CLASSES}
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Prénom et nom"
                  required
                />
              </div>

              <div>
                <label className={LABEL_CLASSES} htmlFor="offre-phone">
                  Votre numéro WhatsApp
                </label>
                <input
                  id="offre-phone"
                  className={INPUT_CLASSES}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex. 07 01 02 03 04"
                  required
                />
              </div>
            </div>

            {submitError ? (
              <p className="mt-5 rounded-sm border border-line bg-cream2 px-3 py-2 font-body text-sm text-ink">
                {submitError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={!formReady || submitting}
              className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-gold px-6 py-4 font-display text-base font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-goldl disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Envoi en cours…" : "Valider ma commande"}
            </button>
            <p className="mt-3 text-center font-body text-xs text-muted">
              Vous serez redirigé vers WhatsApp pour confirmer avec un conseiller.
            </p>
          </form>
        ) : null}
      </div>

      {/* Barre d'action fixe (mobile/tablette) — la barre d'onglets du site
          est masquée derrière elle, d'où le `pb-28` sur le conteneur. */}
      {step === "landing" ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white p-3 lg:hidden">
          <button
            type="button"
            onClick={openForm}
            className="inline-flex w-full items-center justify-center rounded-sm bg-gold px-6 py-4 font-display text-base font-semibold uppercase tracking-wide text-navy transition-colors hover:bg-goldl"
          >
            Commander · {priceLabel ?? ""}
          </button>
        </div>
      ) : null}
    </div>
    </CampaignShell>
  );
}
